// DownloadPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Button,
    Alert,
    Spinner,
    ProgressBar,
    Badge
} from 'react-bootstrap';
import { Download, Shield, Clock, Check, X, RefreshCw, Home } from 'react-feather';
import { motion, AnimatePresence } from 'framer-motion';
import { apifetch } from '../services/interceptors';
import { getSnapshotName } from '../utils/snapshotBranding';

const DownloadPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [tokenInfo, setTokenInfo] = useState(null);
    const [error, setError] = useState(null);
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [downloadComplete, setDownloadComplete] = useState(false);
    
    const snapshotName = getSnapshotName(tokenInfo?.tenant_name);

    // Fetch token info on page load
    useEffect(() => {
        verifyToken();
    }, [token]);
    
    const verifyToken = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await apifetch(`/s2/api/scanner/status/${token}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Invalid or expired token');
            }
            
            const data = await response.json();
            setTokenInfo(data);
            
            if (data.status === 'revoked') {
                throw new Error('This download link has been revoked.');
            }
            
            if (data.status === 'expired') {
                throw new Error('This download link has expired.');
            }
            
            if (data.status === 'limit_reached') {
                throw new Error('Download limit has been reached.');
            }
            
            await getDownloadUrl();
            
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };
    
    const getDownloadUrl = async () => {
        try {
            const response = await apifetch(`/s2/api/scanner/download/${token}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to get download URL');
            }
            
            const data = await response.json();
            setDownloadUrl(data.download_url);
            
            // Update token info with latest counts
            setTokenInfo(prev => ({
                ...prev,
                download_count: data.download_count,
                download_limit: data.download_limit,
                remaining: data.remaining_downloads
            }));
            setLoading(false);
            
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };
    
    // ============================================
    // FRONTEND RENAME: Download with custom filename
    // ============================================
    const startDownload = async () => {
        if (!downloadUrl) {
            setError('Download URL not available');
            return;
        }
        
        setDownloading(true);
        setDownloadProgress(0);
        setDownloadComplete(false);
        setError(null);
        
        try {
            // STEP 1: Increment the download count
            console.log('📊 Incrementing download count...');
            const countResponse = await apifetch(`/s2/api/count/download/${token}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!countResponse.ok) {
                const errorData = await countResponse.json();
                throw new Error(errorData.error || 'Failed to increment download count');
            }
            
            const countData = await countResponse.json();
            console.log(`✅ Download count updated: ${countData.download_count}`);
            
            // Update token info with new count
            setTokenInfo(prev => ({
                ...prev,
                download_count: countData.download_count,
                remaining_downloads: countData.remaining
            }));
            
            // STEP 2: Download the file from S3
            console.log(`📥 Downloading from: ${downloadUrl}`);
            
            // Use fetch to download the file with progress tracking
            const response = await fetch(downloadUrl);
            
            if (!response.ok) {
                throw new Error(`Download failed: ${response.statusText}`);
            }
            
            // Get the content length for progress tracking
            const contentLength = response.headers.get('content-length');
            const total = contentLength ? parseInt(contentLength, 10) : 0;
            let loaded = 0;
            
            // Create a reader for the response body
            const reader = response.body.getReader();
            const chunks = [];
            
            // Read the stream
            while (true) {
                const { done, value } = await reader.read();
                
                if (done) {
                    break;
                }
                
                chunks.push(value);
                loaded += value.length;
                
                // Update progress
                if (total > 0) {
                    const progress = Math.round((loaded / total) * 100);
                    setDownloadProgress(Math.min(progress, 100));
                } else {
                    // If content-length is not available, use a simulated progress
                    setDownloadProgress(prev => Math.min(prev + 5, 95));
                }
            }
            
            // Combine all chunks into a single blob
            const blob = new Blob(chunks);
            
            // Create a download link
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `Pipsqueak_${token}.exe`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up
            setTimeout(() => URL.revokeObjectURL(link.href), 1000);
            
            setDownloadProgress(100);
            setDownloadComplete(true);
            setDownloading(false);
            
            console.log(`✅ Download completed: Pipsqueak_${token}.exe`);
            
        } catch (error) {
            console.error('❌ Download error:', error);
            setError(error.message || 'Failed to download file. Please try again.');
            setDownloading(false);
        }
    };
    
    // Simulate download progress for better UX
    const handleDownload = () => {
        // Start progress animation
        let progress = 0;
        const interval = setInterval(() => {
            progress += Math.random() * 15 + 5;
            if (progress >= 95) {
                clearInterval(interval);
                setDownloadProgress(95);
            } else {
                setDownloadProgress(Math.min(progress, 95));
            }
        }, 200);
        
        // Start actual download
        startDownload().finally(() => {
            clearInterval(interval);
            setDownloadProgress(100);
        });
    };
    
    const handleRetry = () => {
        setError(null);
        setDownloadComplete(false);
        setDownloadUrl(null);
        setDownloadProgress(0);
        setDownloading(false);
        verifyToken();
    };
    
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-100">
                <div className="text-center">
                    <Spinner animation="border" variant="primary" size="lg" />
                    <p className="mt-3 text-muted">Verifying download link...</p>
                </div>
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="container mt-5">
                <Card className="shadow-lg border-0">
                    <Card.Body className="p-5 text-center">
                        <X size={48} className="text-danger mb-3" />
                        <h4 className="text-danger">Download Failed</h4>
                        <p className="text-muted">{error}</p>
                        <Button variant="primary" onClick={handleRetry}>
                            <RefreshCw size={16} className="me-2" />
                            Try Again
                        </Button>
                        <Button 
                            variant="outline-secondary" 
                            className="ms-2"
                            onClick={() => navigate('/')}
                        >
                            Go Home
                        </Button>
                    </Card.Body>
                </Card>
            </div>
        );
    }
    
    return (
        <div className="container mt-5">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Card className="shadow-lg border-0">
                    <Card.Body className="p-5">
                        <div className="text-center mb-4">
                            <div className="bg-primary bg-opacity-10 rounded-circle p-3 d-inline-block mb-3">
                                <Shield size={40} className="text-primary" />
                            </div>
                            <h3 className="fw-bold">{snapshotName}</h3>
                            <p className="text-muted">
                                Your {snapshotName} is ready to download. Click the button below to start.
                            </p>
                        </div>
                        
                        {/* Token Information */}
                        {tokenInfo && (
                            <div className="bg-light rounded p-3 mb-4">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <Home size={16} className="text-primary me-2" />
                                            <div>
                                                <small className="text-muted d-block">Organization</small>
                                                <strong>{tokenInfo.org_name}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <Clock size={16} className="text-primary me-2" />
                                            <div>
                                                <small className="text-muted d-block">Status</small>
                                                <Badge bg={tokenInfo.status === 'active' ? 'success' : 'warning'}>
                                                    {tokenInfo.status === 'active' ? 'Active' : tokenInfo.status}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <Download size={16} className="text-primary me-2" />
                                            <div>
                                                <small className="text-muted d-block">Downloads Remaining</small>
                                                <strong>
                                                    {tokenInfo.download_limit === 999999 
                                                        ? 'Unlimited' 
                                                        : `${tokenInfo.download_limit - (tokenInfo.download_count || 0)}`}
                                                </strong>
                                                <span className="text-muted ms-1">
                                                    / {tokenInfo.download_limit === 999999 ? '∞' : tokenInfo.download_limit}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <Clock size={16} className="text-primary me-2" />
                                            <div>
                                                <small className="text-muted d-block">Expires</small>
                                                <strong>
                                                    {tokenInfo.expires_at 
                                                        ? new Date(tokenInfo.expires_at).toLocaleString()
                                                        : 'Never'}
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Download Info - Shows the filename format */}
                        <div className="bg-info bg-opacity-10 rounded p-3 mb-4">
                            <small className="text-info d-flex align-items-center">
                                <Shield size={14} className="me-2" />
                                <span>
                                    <strong>Filename:</strong> Pipsqueak_{token}.exe
                                    <span className="ms-2 text-muted">
                                        (Your token is embedded in the filename)
                                    </span>
                                </span>
                            </small>
                        </div>
                        
                        {/* Download Button */}
                        {!downloadComplete ? (
                            <div className="text-center">
                                {downloadUrl && !downloading && (
                                    <>
                                        <p className="text-muted mb-3">
                                            Click the button below to start your download.
                                        </p>
                                        <Button 
                                            variant="primary" 
                                            size="lg" 
                                            className="px-5 py-3"
                                            onClick={handleDownload}
                                            disabled={downloading}
                                        >
                                            <Download size={20} className="me-2" />
                                            Download Snapshot
                                        </Button>
                                        <p className="text-muted small mt-2">
                                            <strong>File:</strong> Pipsqueak_{token}.exe
                                            {tokenInfo?.download_limit !== 999999 && (
                                                <span className="ms-2">
                                                    ({tokenInfo?.download_limit - (tokenInfo?.download_count || 0)} downloads remaining)
                                                </span>
                                            )}
                                            {tokenInfo?.download_limit === 999999 && (
                                                <span className="ms-2">(Unlimited downloads)</span>
                                            )}
                                        </p>
                                    </>
                                )}
                                
                                {downloading && (
                                    <div className="w-100">
                                        <p className="text-muted mb-2">
                                            Downloading Pipsqueak_{token}.exe...
                                        </p>
                                        <ProgressBar 
                                            now={downloadProgress} 
                                            label={`${Math.round(downloadProgress)}%`}
                                            animated
                                            variant="primary"
                                            className="mb-2"
                                            style={{ height: '25px' }}
                                        />
                                        <p className="text-muted small">
                                            Your file will be saved with the correct filename automatically.
                                        </p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center">
                                <div className="bg-success bg-opacity-10 rounded-circle p-3 d-inline-block mb-3">
                                    <Check size={40} className="text-success" />
                                </div>
                                <h5 className="text-success">Download Complete!</h5>
                                <p className="text-muted">
                                    Your scanner has been downloaded as: <strong>Pipsqueak_{token}.exe</strong>
                                </p>
                                <div className="d-flex justify-content-center gap-2 flex-wrap">
                                    <Button 
                                        variant="outline-primary"
                                        onClick={handleDownload}
                                    >
                                        <Download size={16} className="me-2" />
                                        Download Again
                                    </Button>
                                    <Button 
                                        variant="outline-secondary"
                                        onClick={() => navigate('/')}
                                    >
                                        Go Home
                                    </Button>
                                </div>
                                <div className="mt-3 p-3 bg-warning bg-opacity-10 rounded">
                                    <small className="text-warning">
                                        ⚠️ The scanner will automatically use the token from its filename.
                                    </small>
                                </div>
                            </div>
                        )}
                        
                        {/* Footer */}
                        <div className="mt-4 pt-3 border-top">
                            <small className="text-muted">
                                <strong>Security Note:</strong> The scanner reads its token from the filename 
                                <strong> Pipsqueak_{token}.exe</strong>. This link is valid for 5 minutes and can be used 
                                {tokenInfo?.download_limit === 999999 
                                    ? ' an unlimited number of times.' 
                                    : ` ${tokenInfo?.download_limit - (tokenInfo?.download_count || 0)} more time(s).`}
                            </small>
                        </div>
                    </Card.Body>
                </Card>
            </motion.div>
        </div>
    );
};

export default DownloadPage;